import { apiUrl } from "../api/endpoints";
import React, { useState, useContext, useMemo, useEffect } from 'react';
import '../Components/modal.css';
import './edit.css';
import './DownloadReportShared.css';

import axios from 'axios';

import Select from 'react-select';

import { useAuth } from '../hooks/AuthProvider';
import { LanguageContext } from '../context/LanguageContext';
import downloadReportTranslations from '../languages/downloadReport.json';

import Alert from '../Components/Alert';
import AiReportModePanel from '../Components/AiReportModePanel';
import AiReportVariablesStep from '../Components/AiReportVariablesStep';
import { getErrorMessage } from '../utils/errorUtils';
import { isReportDownloadDatastream, sortDatastreamsByReportPriority } from '../utils/datastreamValidation';
import {
  buildMissingAiReportParamsMessage,
  getAiReportTypeConfig,
  getMissingRequiredAiReportParams,
  mergeDefaultAiReportVariables,
  sanitizeAiReportVariablesForType,
  useAiReportTypes,
} from '../utils/aiReportTypes';
import {
  buildReportAccess,
  downloadFileFromUrl,
  getReportModeUi,
  isNoDataReportResponse,
  modeIncludesAiReport,
  REPORT_DOWNLOAD_MODES,
  resolveAiReportMode,
  resolveReportMode,
} from '../utils/reportDownload';

import dayjs from 'dayjs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { CustomSwitch } from '../svg/UtilsIcons';
import WorkDaysHoursPicker, { normalizeWorkDaysHours } from '../Components/WorkDaysHoursPicker';
import { resolveBrowserTimezone, zonedDateTimeToUnix } from '../utils/timezone';
import {
  applyReportBoundaryDefault,
  buildReportEndForStart,
  isReportEndAfterStart,
  REPORT_END_INCLUSIVE_MINUTE_FLAG,
} from '../utils/reportDateRange';

const DEFAULT_NO_DATA_MESSAGE =
  'There is no data available for the selected filters and time range.';

export const DownloadReport = ({
  state,
  setState,
  device,
  template,
  setLoadingReport,
  onAlert,
}) => {
  const { permissions, email: authEmail, user } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canDownloadStandardReport = permissions['download_reports'];
  const canDownloadAiReport = permissions['download_ai_reports'];
  const t = downloadReportTranslations[lang].downloadReport;
  const isRtl = ['ar', 'ur'].includes(lang);

  const isDarkTheme =
    document.documentElement.classList.contains('dark-theme') ||
    document.body.classList.contains('dark-theme');

  const pickerTheme = useMemo(
    () => createTheme({ palette: { mode: isDarkTheme ? 'dark' : 'light' } }),
    [isDarkTheme]
  );

  const { types: aiReportTypes, availability: aiReportAvailability } = useAiReportTypes();
  const aiReportsEnabledForDevices = aiReportAvailability.devices !== false;
  const reportAccess = useMemo(
    () =>
      buildReportAccess({
        canDownloadStandardReport,
        canDownloadAiReport: canDownloadAiReport && aiReportsEnabledForDevices,
      }),
    [aiReportsEnabledForDevices, canDownloadAiReport, canDownloadStandardReport]
  );

  const [number, setNumber] = useState('');
  const [tstart, setTstart] = useState('');
  const [tstop, setTstop] = useState('');
  const [sendToEmail, setSendToEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedStreams, setSelectedStreams] = useState([]);
  const [selectedWorkDaysHours, setSelectedWorkDaysHours] = useState([]);
  const [fetchedWorkDaysHours, setFetchedWorkDaysHours] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [reportMode, setReportMode] = useState(reportAccess.defaultMode);
  const [aiReportVariables, setAiReportVariables] = useState({});
  const [showAiReportValidation, setShowAiReportValidation] = useState(false);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const filteredDatastreams = useMemo(() => {
    return sortDatastreamsByReportPriority(template?.datastreams).filter(isReportDownloadDatastream);
  }, [template]);
  const deviceData = device?.data || device || {};
  const deviceRowId = deviceData?.id ?? deviceData?._row_id ?? state?.id ?? 0;
  const entityReportTimezone =
    deviceData?.organization_timezone ||
    deviceData?.organization?.timezone ||
    device?.organization_timezone ||
    device?.organization?.timezone ||
    '';
  const reportTimezone = entityReportTimezone || resolveBrowserTimezone();
  const storedWorkDaysHours = useMemo(() => {
    const fromProps = normalizeWorkDaysHours(device?.data?.work_days_hours || device?.work_days_hours || []);
    if (fromProps.length > 0) return fromProps;
    return normalizeWorkDaysHours(fetchedWorkDaysHours);
  }, [device, fetchedWorkDaysHours]);
  const storedWorkDaysHoursKey = useMemo(
    () => JSON.stringify(storedWorkDaysHours),
    [storedWorkDaysHours]
  );
  const reportType = template?.type || null;
  const defaultAiReportRoom = String(device?.data?.name || device?.data?.did || '').trim();

const defaultEmail = (authEmail || user?.email || '').trim();

useEffect(() => {
  if (sendToEmail) {
    setEmail((prev) => {
      if (prev.trim()) return prev;
      return defaultEmail;
    });
  }
}, [sendToEmail, defaultEmail]);

  useEffect(() => {
    if (state?.open) {
      setReportMode(reportAccess.defaultMode);
      setAiReportVariables(
        mergeDefaultAiReportVariables(aiReportTypes, reportType, {}, { room: defaultAiReportRoom })
      );
      setSelectedWorkDaysHours(JSON.parse(storedWorkDaysHoursKey));
      setShowAiReportValidation(false);
    }
  }, [state?.open, reportAccess.defaultMode, aiReportTypes, reportType, defaultAiReportRoom, storedWorkDaysHoursKey]);

  useEffect(() => {
    if (!state?.open || !deviceRowId) return undefined;

    let active = true;
    setScheduleLoading(true);
    axios
      .get(apiUrl(`device/get/${deviceRowId}`))
      .then((response) => {
        if (!active) return;
        const rows = normalizeWorkDaysHours(response?.data?.data?.work_days_hours || []);
        setFetchedWorkDaysHours(rows);
        setSelectedWorkDaysHours(rows);
      })
      .catch((error) => {
        console.warn('[DownloadReport] Failed to load device work days/hours:', error);
      })
      .finally(() => {
        if (active) setScheduleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [state?.open, deviceRowId]);

  useEffect(() => {
    if (!state?.open) return;
    setAiReportVariables((prev) =>
      mergeDefaultAiReportVariables(aiReportTypes, reportType, prev, { room: defaultAiReportRoom })
    );
  }, [state?.open, aiReportTypes, reportType, defaultAiReportRoom]);

  const resolvedReportMode = resolveReportMode(reportAccess, reportMode);
  const noReportPermission = reportAccess.noReportPermission;
  const missingAiReportParams = useMemo(
    () =>
      modeIncludesAiReport(resolvedReportMode)
        ? getMissingRequiredAiReportParams(aiReportTypes, reportType, aiReportVariables)
        : [],
    [aiReportTypes, aiReportVariables, reportType, resolvedReportMode]
  );
  const handleAiReportVariableChange = (fieldName, value) => {
    setAiReportVariables((prev) => ({ ...prev, [fieldName]: value }));
  };

  const activeReportSchedule = selectedWorkDaysHours.length > 0
    ? selectedWorkDaysHours
    : storedWorkDaysHours;

  const handleStartChange = (newValue) => {
    if (!newValue) {
      setTstart('');
      return;
    }

    const nextStart = applyReportBoundaryDefault({
      nextValue: newValue,
      currentValue: tstart,
      boundary: 'start',
      schedule: activeReportSchedule,
    });
    setTstart(nextStart);

    if (tstop && !isReportEndAfterStart(nextStart, tstop)) {
      setTstop(buildReportEndForStart(nextStart, activeReportSchedule));
    }
  };

  const handleEndChange = (newValue) => {
    if (!newValue) {
      setTstop('');
      return;
    }

    const nextEnd = applyReportBoundaryDefault({
      nextValue: newValue,
      currentValue: tstop,
      boundary: 'end',
      schedule: activeReportSchedule,
    });

    if (tstart && !isReportEndAfterStart(tstart, nextEnd)) return;
    setTstop(nextEnd);
  };

  const showAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      onAlert(type, message);
      return;
    }

    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
  };

  const closeReportDialog = () => setState({ open: false, id: 0 });

  const closeDialogWithAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      closeReportDialog();
      onAlert(type, message);
      return;
    }

    showAlert(type, message);
    setTimeout(() => closeReportDialog(), 2000);
  };

  const parseLocalDateTimeToUnix = (dateTimeStr) => zonedDateTimeToUnix(dateTimeStr, reportTimezone);

  const handleDownload = async () => {
    if (noReportPermission) {
      return showAlert('error', t.noReportPermissionMessage || 'You do not have access to download reports.');
    }

    if (!number || isNaN(number) || number <= 0) {
      return showAlert('error', t.alertInvalidNumber);
    }
    if (!tstart || !tstop) {
      return showAlert('error', t.alertSelectDates);
    }

    const tstartUnix = parseLocalDateTimeToUnix(tstart);
    const tstopUnix = parseLocalDateTimeToUnix(tstop);

    if (tstartUnix >= tstopUnix) {
      return showAlert('error', t.alertTimeOrder);
    }

    if (selectedStreams.length === 0) {
      return showAlert('error', t.alertSelectDatastream);
    }

    if (storedWorkDaysHours.length > 0 && selectedWorkDaysHours.length === 0) {
      return showAlert('error', t.alertSelectWorkDaysHours || 'Please select at least one configured work day/hour for this report.');
    }

    if (sendToEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        return showAlert('error', t.alertInvalidEmail);
      }
    }

    const shouldUseAiReport = resolveAiReportMode(reportAccess, reportMode);
    const aiTypeConfig = shouldUseAiReport ? getAiReportTypeConfig(aiReportTypes, reportType) : null;

    if (shouldUseAiReport && !aiTypeConfig) {
      return showAlert('error', 'Please choose an AI report type for this template before generating an AI report.');
    }

    const normalizedAiVariables = sanitizeAiReportVariablesForType(aiReportTypes, reportType, aiReportVariables);
    if (shouldUseAiReport) {
      const missingVars = getMissingRequiredAiReportParams(aiReportTypes, reportType, aiReportVariables);
      if (missingVars.length > 0) {
        setShowAiReportValidation(true);
        return showAlert(
          'error',
          buildMissingAiReportParamsMessage(missingVars, t.aiReportVariables)
        );
      }
    }

    const reportModeUi = getReportModeUi(resolvedReportMode, t.reportModeUi);
    const body = {
      bucket: 'Sensors',
      measurement: template.tid,
      device: device.data.did,
      deviceName: device.data.name,
      fields: filteredDatastreams
        .filter((ds) => selectedStreams.includes(ds.pin))
        .map((ds) => ({
          field: ds.pin,
          label: ds.name,
          min: ds.min,
          max: ds.max,
          dataType: ds.dataType ?? ds.datatype ?? ds.data_type,
        })),
      tstart: tstartUnix,
      tstop: tstopUnix,
      step: number,
      sendToEmail,
      email,
      reportMode: resolvedReportMode,
      includeAiReport: shouldUseAiReport,
      aiReportType: reportType,
      aiReportVariables: normalizedAiVariables,
      work_days_hours: selectedWorkDaysHours,
      report_timezone: entityReportTimezone || undefined,
      tstart_local: tstart,
      tstop_local: tstop,
      [REPORT_END_INCLUSIVE_MINUTE_FLAG]: true,
    };

    setLoadingReport(true);

    try {
      const response = await axios.post(apiUrl(`data/download_report`), body);
      const responseData = response.data || {};

      if (isNoDataReportResponse(responseData)) {
        showAlert('info', responseData.message || t.noDataMessage || DEFAULT_NO_DATA_MESSAGE);
        return;
      }

      const { status, downloadUrl, reportMode: backendReportMode, downloadType } = responseData;
      const effectiveBackendMode =
        backendReportMode ||
        (downloadType === 'pdf' || /\.pdf($|\?)/i.test(String(downloadUrl || ''))
          ? REPORT_DOWNLOAD_MODES.AI
          : REPORT_DOWNLOAD_MODES.STANDARD);

      if (effectiveBackendMode !== resolvedReportMode) {
        throw new Error(
          'The report was generated in an unexpected format. Please try again.'
        );
      }

      if (sendToEmail) {
        closeDialogWithAlert(
          'success',
          `${reportModeUi.sendToEmailLabel} It will be sent to ${email} shortly.`
        );
      } else if (status === 'ready' && downloadUrl) {
        try {
          await downloadFileFromUrl(downloadUrl);
          closeDialogWithAlert('success', reportModeUi.successLabel);
        } catch (downloadError) {
          const downloadErrorMessage = getErrorMessage(downloadError);
          showAlert(
            'error',
            `Failed to download the ${reportModeUi.nounLabel} file: ${downloadErrorMessage}`
          );
        }
      } else {
        closeDialogWithAlert('warning', t.reportPreparing);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        `Could not generate the ${reportModeUi.nounLabel}. Please try again later.`
      );
      showAlert('error', errorMessage);
    } finally {
      setLoadingReport(false);
    }
  };

  const maxDateTime = dayjs().endOf('day');
  const startValue = tstart ? dayjs(tstart) : null;
  const stopValue = tstop ? dayjs(tstop) : null;
  const primaryActionLabel = getReportModeUi(resolvedReportMode, t.reportModeUi).actionLabel;

  if (!state?.open) return null;

  if (!reportAccess.canOpenReportDialog) {
    return (
      <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="dr-popup dr-popup--autoHeight" id="popup">
          <div className="dr-header modal-header">
            <h2 className="dr-title">{t.permissionDeniedTitle}</h2>
          </div>

          <div className="dr-content modal-content">
            <p>{t.permissionDeniedMessage}</p>
          </div>

          <div className="dr-footer modal-footer">
            <button className="btn btn-cancel" onClick={() => setState({ open: false, id: 0 })}>
              {t.buttonClose}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {typeof onAlert !== 'function' && (
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />
      )}

      <ThemeProvider theme={pickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="dr-popup" id="popup">
              <div className="dr-header modal-header">
                <h2 className="dr-title">{t.title}</h2>
              </div>

              <div className="dr-content modal-content">
                {noReportPermission && (
                  <div className="dr-permissionNotice" role="alert">
                    {t.noReportPermissionMessage || 'You do not have access to download this report. The download button is disabled.'}
                  </div>
                )}

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelStart}</label>

                  <div className="dr-muiField">
                    <DateTimePicker
                      value={startValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={handleStartChange}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: t.placeholderDateTime,
                        },
                        popper: {
                          className: 'dr-pickerPopper',
                        },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelEnd}</label>

                  <div className="dr-muiField">
                    <DateTimePicker
                      value={stopValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={handleEndChange}
                      minDateTime={startValue || undefined}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: t.placeholderDateTime,
                        },
                        popper: {
                          className: 'dr-pickerPopper',
                        },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelDatastreams}</label>

                  <Select
                    isMulti
                    isRtl={isRtl}
                    classNamePrefix="dr-select"
                    options={[
                      { value: '__ALL__', label: t.optionAll },
                      ...filteredDatastreams.map((ds) => ({
                        value: ds.pin,
                        label: `${ds.name} (${ds.pin})`,
                      })),
                    ]}
                    value={
                      selectedStreams.length === filteredDatastreams.length && filteredDatastreams.length > 0
                        ? [{ value: '__ALL__', label: t.optionAll }]
                        : selectedStreams
                            .map((pin) => {
                              const stream = filteredDatastreams.find((ds) => ds.pin === pin);
                              return stream ? { value: pin, label: `${stream.name} (${pin})` } : null;
                            })
                            .filter(Boolean)
                    }
                    onChange={(selectedOptions) => {
                      const safeOptions = selectedOptions || [];
                      const isAllSelected = safeOptions.some((opt) => opt.value === '__ALL__');
                      if (isAllSelected) {
                        setSelectedStreams(filteredDatastreams.map((ds) => ds.pin));
                      } else {
                        setSelectedStreams(safeOptions.map((opt) => opt.value));
                      }
                    }}
                  />
                </div>

                <div className="dr-field form-item">
                  {storedWorkDaysHours.length > 0 ? (
                    <WorkDaysHoursPicker
                      mode="select"
                      value={selectedWorkDaysHours}
                      availableRows={storedWorkDaysHours}
                      onChange={setSelectedWorkDaysHours}
                      labels={{
                        title: t.workDaysHoursTitle || 'Report schedule',
                        reportWillUse: t.reportWillUse || 'Report will use',
                        available: t.availableWorkDaysHours || 'Available from device settings',
                      }}
                    />
                  ) : (
                    <div className="wdh wdh-default">
                      <div className="wdh-header">
                        <span>{t.workDaysHoursTitle || 'Report schedule'}</span>
                        <span className="wdh-summary">
                          {scheduleLoading
                            ? (t.loadingWorkDaysHours || 'Loading schedule...')
                            : (t.defaultWorkDaysHours || 'All days, 24 hours')}
                        </span>
                      </div>
                      <div className="wdh-available">
                        {scheduleLoading
                          ? (t.loadingWorkDaysHours || 'Loading schedule...')
                          : (t.noWorkDaysHoursSet || 'No work days/hours are configured, so this report uses all days and 24 hours.')}
                      </div>
                    </div>
                  )}
                </div>

                <AiReportModePanel
                  access={reportAccess}
                  selectedMode={reportMode}
                  onModeChange={setReportMode}
                  labels={t.aiReportMode}
                />

                <AiReportVariablesStep
                  reportMode={resolvedReportMode}
                  reportType={reportType}
                  types={aiReportTypes}
                  variables={aiReportVariables}
                  onVariableChange={handleAiReportVariableChange}
                  missingFields={missingAiReportParams.map((param) => param.key)}
                  showValidation={showAiReportValidation}
                  labels={t.aiReportVariables}
                />

                <div className="dr-toggleRow form-item">
                  <label className="dr-label form-lbl">{t.labelSendToEmail}</label>

                  <CustomSwitch checked={sendToEmail} onChange={(nextChecked) => setSendToEmail(nextChecked)} />
                </div>

                {sendToEmail && (
                  <div className="dr-field form-item">
                    <label className="dr-label form-lbl">{t.labelEmail}</label>
                    <input
                      type="email"
                      className="dr-input add-column"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.placeholderEmail}
                    />
                  </div>
                )}

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelDataAggregation}</label>

                  <div className="dr-pillRow details-time-range">
                    {t.aggregationOptions.map((range, idx) => (
                      <span
                        key={idx}
                        className={`dr-pill details-control-btn ${number === range.value ? 'is-active active' : ''}`}
                        onClick={() => setNumber(range.value)}
                      >
                        {range.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dr-footer modal-footer">
                <button className="btn btn-cancel" onClick={closeReportDialog}>
                  {t.buttonCancel}
                </button>

                <button
                  className="btn btn-apply dr-btnApply"
                  onClick={handleDownload}
                  disabled={noReportPermission}
                  title={noReportPermission ? (t.noReportPermissionMessage || 'You do not have access to download reports.') : undefined}
                >
                  {primaryActionLabel}
                </button>
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </ThemeProvider>
    </React.Fragment>
  );
};
